import React, { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import Dialog from "@/src/components/dialog/Dialog";
import { ChipInputField } from "@/src/components/form-fields/ChipInputField";
import { SwitchField } from "@/src/components/form-fields/SwitchField";
import { TextField } from "@/src/components/form-fields/TextField";
import { useEmbedProfileMutations } from "@/src/hooks/mutations/useEmbedProfileMutations";
import { applyApiFieldErrors } from "@/src/utils/applyApiFieldErrors";

import { isValidEmbedDomain, normalizeEmbedDomain } from "./embedDomains";
import { buildEmbedProfileSchema } from "./embedProfileSchema";

import type { JSX } from "react";

import type {
  EmbedProfile,
  EmbedProfilePayload,
} from "@/src/api/ProjectAPI.types";

import type { EmbedProfileFormValues } from "./embedProfileSchema";

const API_FIELD_MAP: Record<string, keyof EmbedProfileFormValues> = {
  label: "label",
  domains: "domains",
};

const EMPTY_VALUES: EmbedProfileFormValues = {
  label: "",
  domains: [],
  is_default: false,
};

function buildPayload(values: EmbedProfileFormValues): EmbedProfilePayload {
  return {
    label: values.label,
    domains: values.domains,
    default: values.is_default,
  };
}

interface EmbedProfileFormDialogProps {
  open: boolean;
  profile: EmbedProfile | null;
  currentDefault: EmbedProfile | null;
  onClose: () => void;
}

export default function EmbedProfileFormDialog({
  open,
  profile,
  currentDefault,
  onClose,
}: EmbedProfileFormDialogProps): JSX.Element {
  const { t } = useTranslation("editor-organization");
  const { createEmbedProfile, updateEmbedProfile, isSaving } =
    useEmbedProfileMutations();

  const schema = useMemo(() => buildEmbedProfileSchema(), []);
  const methods = useForm<EmbedProfileFormValues>({
    defaultValues: EMPTY_VALUES,
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (open) {
      methods.reset(
        profile
          ? {
              label: profile.label,
              domains: profile.domains,
              is_default: profile.default,
            }
          : EMPTY_VALUES,
      );
    }
  }, [open, profile, methods]);

  const translateFieldError = (field: string, message: string): string =>
    field === "label" && message.includes("taken")
      ? t("embedProfiles.form.errors.labelTaken")
      : message;

  const onValid = async (values: EmbedProfileFormValues): Promise<void> => {
    const payload = buildPayload(values);

    try {
      if (profile) {
        await updateEmbedProfile({ uuid: profile.uuid, data: payload });
      } else {
        await createEmbedProfile(payload);
      }
      onClose();
    } catch (error) {
      const applied = applyApiFieldErrors(
        error,
        methods.setError,
        API_FIELD_MAP,
        translateFieldError,
      );
      if (!applied) toast.error(t("editor-notif:saveError"));
    }
  };

  const getRemoveLabel = (domain: string): string =>
    t("embedProfiles.form.removeDomain", { domain });

  const validateDomain = (domain: string): string | null =>
    isValidEmbedDomain(domain)
      ? null
      : t("embedProfiles.form.errors.domainInvalid");

  const handleOpenChange = (next: boolean): void => {
    if (!next) onClose();
  };

  const replacedDefault =
    currentDefault && currentDefault.uuid !== profile?.uuid
      ? currentDefault
      : null;
  const defaultDescription = [
    t("embedProfiles.form.defaultHint"),
    replacedDefault
      ? t("embedProfiles.form.defaultReplaces", {
          label: replacedDefault.label,
        })
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <Dialog
        cancelLabel={t("editor-offers:cancel")}
        confirmLabel={t("editor-offers:save")}
        contentClassName="sm:max-w-lg"
        isConfirmDisabled={isSaving}
        isOpen={open}
        onConfirm={methods.handleSubmit(onValid)}
        onOpenChange={handleOpenChange}
        title={
          profile
            ? t("embedProfiles.form.editTitle")
            : t("embedProfiles.form.title")
        }
      >
        <div className="flex flex-col gap-4">
          <TextField<EmbedProfileFormValues>
            label={t("embedProfiles.form.label")}
            name="label"
            placeholder={t("embedProfiles.form.labelPlaceholder")}
          />
          <SwitchField<EmbedProfileFormValues>
            description={defaultDescription}
            label={t("embedProfiles.form.isDefault")}
            name="is_default"
          />
          <ChipInputField<EmbedProfileFormValues>
            description={t("embedProfiles.form.domainsHint")}
            getRemoveLabel={getRemoveLabel}
            label={t("embedProfiles.form.domains")}
            name="domains"
            normalize={normalizeEmbedDomain}
            placeholder={t("embedProfiles.form.domainsPlaceholder")}
            validate={validateDomain}
          />
        </div>
      </Dialog>
    </FormProvider>
  );
}
